/**
 * DIEUKYDIEU.TV - CHIP HORIZONTAL SCROLL AFFORDANCE MODULE (js/chip-scroll.js)
 * Provides liquid glass floating scroll arrow buttons and gradient edge fade masks for horizontal chip tracks.
 * (< 160 lines)
 */

const activeInstances = new Set();

/**
 * Initializes scroll buttons and fade masks on a chip track or wrapper.
 * @param {HTMLElement} element - A wrapper element or a scroll track element.
 * @returns {object|null} Controller object with `update` and `destroy` methods.
 */
export function initChipScroll(element) {
    if (!element || !(element instanceof HTMLElement)) return null;

    // Resolve wrapper and track elements
    let wrapper = element.classList.contains('chip-scroll-wrapper') ? element : element.closest('.chip-scroll-wrapper');
    let track = element.classList.contains('chip-scroll-track')
        ? element
        : element.querySelector('.chip-scroll-track, .sec-subchips-scroll, .chips-tier-1-scroll, .chips-tier-2-inner, .apple-chip-track');

    if (!wrapper && !track) {
        track = element;
        wrapper = element.parentElement;
        if (wrapper) wrapper.classList.add('chip-scroll-wrapper');
    } else if (wrapper && !track) {
        track = wrapper.firstElementChild;
    }

    if (!track || !wrapper) return null;

    // Cleanup previous instance if attached to this track
    if (track._chipScrollInstance) {
        track._chipScrollInstance.destroy();
    }

    wrapper.classList.add('chip-scroll-wrapper');
    track.classList.add('chip-scroll-track');

    // 1. Left Fade Mask
    let leftMask = wrapper.querySelector(':scope > .chip-fade-left');
    if (!leftMask) {
        leftMask = document.createElement('div');
        leftMask.className = 'chip-fade-mask chip-fade-left';
        leftMask.setAttribute('aria-hidden', 'true');
        wrapper.insertBefore(leftMask, track);
    }

    // 2. Left Scroll Button
    let leftBtn = wrapper.querySelector(':scope > .chip-scroll-left');
    if (!leftBtn) {
        leftBtn = document.createElement('button');
        leftBtn.className = 'chip-scroll-arrow chip-scroll-left';
        leftBtn.type = 'button';
        leftBtn.tabIndex = -1;
        leftBtn.setAttribute('aria-label', 'Cuộn danh mục sang trái');
        leftBtn.innerHTML = '<span class="material-symbols-rounded">chevron_left</span>';
        wrapper.insertBefore(leftBtn, track);
    }

    // 3. Right Scroll Button
    let rightBtn = wrapper.querySelector(':scope > .chip-scroll-right');
    if (!rightBtn) {
        rightBtn = document.createElement('button');
        rightBtn.className = 'chip-scroll-arrow chip-scroll-right';
        rightBtn.type = 'button';
        rightBtn.tabIndex = -1;
        rightBtn.setAttribute('aria-label', 'Cuộn danh mục sang phải');
        rightBtn.innerHTML = '<span class="material-symbols-rounded">chevron_right</span>';
        wrapper.appendChild(rightBtn);
    }

    // 4. Right Fade Mask
    let rightMask = wrapper.querySelector(':scope > .chip-fade-right');
    if (!rightMask) {
        rightMask = document.createElement('div');
        rightMask.className = 'chip-fade-mask chip-fade-right';
        rightMask.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(rightMask);
    }

    // Update visibility logic
    const updateVisibility = () => {
        if (!track || !track.isConnected) return;
        const scrollLeft = track.scrollLeft;
        const scrollWidth = track.scrollWidth;
        const clientWidth = track.clientWidth;
        const maxScroll = Math.max(0, scrollWidth - clientWidth);

        // If not overflowing at all (or container is hidden / 0 width)
        if (maxScroll <= 4 || clientWidth === 0) {
            leftBtn.classList.remove('visible');
            leftMask.classList.remove('visible');
            rightBtn.classList.remove('visible');
            rightMask.classList.remove('visible');
            return;
        }

        // Left button & mask: only visible when scrolled right
        const showLeft = scrollLeft > 6;
        leftBtn.classList.toggle('visible', showLeft);
        leftMask.classList.toggle('visible', showLeft);

        // Right button & mask: only visible when there is more content to the right
        const showRight = scrollLeft < maxScroll - 6;
        rightBtn.classList.toggle('visible', showRight);
        rightMask.classList.toggle('visible', showRight);
    };

    const getScrollStep = () => Math.max(160, Math.floor(track.clientWidth * 0.65));

    const onLeftClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        track.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
    };

    const onRightClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        track.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
    };

    leftBtn.addEventListener('click', onLeftClick);
    rightBtn.addEventListener('click', onRightClick);

    const onScroll = () => {
        updateVisibility();
    };
    track.addEventListener('scroll', onScroll, { passive: true });

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
            updateVisibility();
        });
        resizeObserver.observe(track);
        resizeObserver.observe(wrapper);
    }

    const onWinResize = () => updateVisibility();
    window.addEventListener('resize', onWinResize, { passive: true });

    // Initial updates with microtask & animation frames for layout calculation
    updateVisibility();
    requestAnimationFrame(updateVisibility);
    setTimeout(updateVisibility, 150);

    const instance = {
        update: updateVisibility,
        destroy: () => {
            leftBtn.removeEventListener('click', onLeftClick);
            rightBtn.removeEventListener('click', onRightClick);
            track.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onWinResize);
            if (resizeObserver) resizeObserver.disconnect();
            activeInstances.delete(instance);
            delete track._chipScrollInstance;
        }
    };

    track._chipScrollInstance = instance;
    activeInstances.add(instance);

    return instance;
}

/**
 * Triggers visibility recalculation across all active chip scroll containers.
 */
export function refreshAllChipScrolls() {
    activeInstances.forEach((inst) => {
        try {
            inst.update();
        } catch {
            // Safe fallback
        }
    });
}
